/**
 * Feature flags for account-ui.
 *
 * Unleash-driven, mirroring the ebs platform's `@shared/feature-flags` pattern.
 * The reusable machinery lives in `src/feature-flags`; this file owns only this
 * app's flag **names**, its **bootstrap** (outage fallback), and any
 * app-specific hooks.
 *
 * account-ui shares one Unleash project with the ebs platform, so flag names
 * are a shared namespace — prefix every account-ui flag with `account-` to
 * avoid colliding with an ebs flag of the same name.
 *
 * Add a flag:
 *   1. Add a name constant + bootstrap entry + hook here.
 *   2. Add the bootstrap entry to `accountFlagBootstrap` (already wired into the
 *      FlagProvider in App.tsx).
 *   3. Create the flag in Unleash (project `default`) and enable it for the
 *      environment(s) the frontend token targets.
 *   4. Gate the code with the hook; choose the outage fallback deliberately
 *      (ON for a kill-switch, OFF for an additive/fail-closed gate).
 */
import { toggle, useFeatureFlag } from 'src/feature-flags'
import type { IToggle } from 'src/feature-flags'

/**
 * Example flag — scaffold only. Replace with a real feature gate when one
 * lands, following this same shape. Defaults OFF so an Unleash outage /
 * unconfigured client keeps the gated surface hidden (fail closed).
 */
export const FEATURE_EXAMPLE_FLAG = 'account-feature-example'

/**
 * Domains — gates the workspace Domains nav item + `/workspaces/:id/domains`
 * route. Defaults OFF so the surface stays hidden (and the route un-reachable)
 * until the domains backend is live and the flag is enabled for the target
 * environment.
 */
export const FEATURE_DOMAINS_FLAG = 'account-feature-domains'

/**
 * Billing — gates the workspace Billing nav item + `/workspaces/:id/billing`
 * route. Defaults OFF until the billing backend is live.
 */
export const FEATURE_BILLING_FLAG = 'account-feature-billing'

/**
 * Company Graph — gates the workspace Company Graph nav item +
 * `/workspaces/:id/graph` route. The feature is implemented but in preview;
 * defaults OFF for a controlled rollout.
 */
export const FEATURE_COMPANY_GRAPH_FLAG = 'account-feature-company-graph'

/**
 * Agents — gates the workspace AI Agents nav item + `/workspaces/:id/agents`
 * route tree (list, new, detail, edit). Backed by the real Squad agent API
 * (`src/services/squadAgent.ts`), not by Core and not by mock data; defaults
 * OFF for a controlled rollout.
 */
export const FEATURE_AGENTS_FLAG = 'account-feature-agents'

/**
 * Home AI — gates the workspace Home nav item + `/workspaces/:id/home` route,
 * i.e. the Core AI assistant chat. The feature is live, but defaults OFF for a
 * controlled rollout: while OFF the Home surface is hidden and the workspace
 * lands on Overview instead (Home can't fall back to itself). Enable in Unleash
 * per environment / org to expose the assistant.
 */
export const FEATURE_HOME_AI_FLAG = 'account-feature-home-ai'

/**
 * Telemetry — gates the static/analytics cards on the workspace Overview that
 * are not yet backed by real APIs: security signals, plan usage, apps-health
 * ("Need attention") and the Overview "soon" stat tiles. Defaults OFF so v1
 * shows only the real, API-backed Overview content. (P1 scope gating.)
 */
export const FEATURE_TELEMETRY_FLAG = 'account-feature-telemetry'

/**
 * Auth policy — gates the workspace Authentication "Policy" tab (policy
 * enforcement + risk-based rules), which is static-only. Defaults OFF until a
 * real policy backend lands. Custom providers / SCIM are NOT gated (they use
 * live APIs).
 */
export const FEATURE_AUTH_POLICY_FLAG = 'account-feature-auth-policy'

/**
 * App webhooks — gates the app-detail "Webhooks" tab. The backend exposes only
 * a single webhook config (not the list UI here), so this stays OFF until P7
 * reconciles the contract.
 */
export const FEATURE_APP_WEBHOOKS_FLAG = 'account-feature-app-webhooks'

/**
 * Roles — gates the workspace Roles nav item, the `/workspaces/:id/roles` route
 * tree (list + role detail) and the Overview "Roles" stat tile. The feature is
 * live and API-backed; defaults OFF for a controlled rollout.
 */
export const FEATURE_ROLES_FLAG = 'account-feature-roles'

/**
 * Public access — gates the workspace Public Access nav item + the
 * `/workspaces/:id/public-access` route tree (credential list + detail). It
 * shares the "Developers" nav group with Domains, and the sidebar drops a group
 * once every item in it is hidden, so with both flags OFF the whole Developers
 * group disappears rather than rendering an empty header. Defaults OFF for a
 * controlled rollout.
 */
export const FEATURE_PUBLIC_ACCESS_FLAG = 'account-feature-public-access'

/**
 * AI connectors — gates the workspace AI connectors nav item + the
 * `/workspaces/:id/ai-connectors` route (MCP for third-party AI clients,
 * TS-CORE-039). The consent page `/connect/consent/:requestId` is NOT gated:
 * Core decides when to send a browser there. Defaults OFF.
 */
export const FEATURE_MCP_CONNECTORS_FLAG = 'account-feature-mcp-connectors'

/**
 * Bootstrap defaults the FlagProvider seeds for account-ui's Unleash flags —
 * the outage fallback. Keep each entry at its flag's safe default.
 */
export const accountFlagBootstrap: IToggle[] = [
  toggle(FEATURE_EXAMPLE_FLAG, false),
  toggle(FEATURE_DOMAINS_FLAG, false),
  toggle(FEATURE_BILLING_FLAG, false),
  toggle(FEATURE_COMPANY_GRAPH_FLAG, false),
  toggle(FEATURE_AGENTS_FLAG, false),
  toggle(FEATURE_HOME_AI_FLAG, false),
  toggle(FEATURE_TELEMETRY_FLAG, false),
  toggle(FEATURE_AUTH_POLICY_FLAG, false),
  toggle(FEATURE_APP_WEBHOOKS_FLAG, false),
  toggle(FEATURE_ROLES_FLAG, false),
  toggle(FEATURE_PUBLIC_ACCESS_FLAG, false),
  toggle(FEATURE_MCP_CONNECTORS_FLAG, false)
]

/**
 * Whether the example feature is enabled for this session. Defaults OFF when
 * the Unleash client is unreachable/unconfigured.
 */
export const useFeatureExample = (): boolean =>
  useFeatureFlag(FEATURE_EXAMPLE_FLAG, false)

/** Whether the workspace Domains feature is enabled. Defaults OFF. */
export const useFeatureDomains = (): boolean =>
  useFeatureFlag(FEATURE_DOMAINS_FLAG, false)

/** Whether the workspace Billing feature is enabled. Defaults OFF. */
export const useFeatureBilling = (): boolean =>
  useFeatureFlag(FEATURE_BILLING_FLAG, false)

/** Whether the workspace Company Graph feature is enabled. Defaults OFF. */
export const useFeatureCompanyGraph = (): boolean =>
  useFeatureFlag(FEATURE_COMPANY_GRAPH_FLAG, false)

/** Whether the workspace Agents feature is enabled. Defaults OFF. */
export const useFeatureAgents = (): boolean =>
  useFeatureFlag(FEATURE_AGENTS_FLAG, false)

/** Whether the workspace Home AI assistant is enabled. Defaults OFF. */
export const useFeatureHomeAi = (): boolean =>
  useFeatureFlag(FEATURE_HOME_AI_FLAG, false)

/** Whether the Overview telemetry/analytics cards are enabled. Defaults OFF. */
export const useFeatureTelemetry = (): boolean =>
  useFeatureFlag(FEATURE_TELEMETRY_FLAG, false)

/** Whether the workspace Authentication Policy tab is enabled. Defaults OFF. */
export const useFeatureAuthPolicy = (): boolean =>
  useFeatureFlag(FEATURE_AUTH_POLICY_FLAG, false)

/** Whether the app-detail Webhooks tab is enabled. Defaults OFF. */
export const useFeatureAppWebhooks = (): boolean =>
  useFeatureFlag(FEATURE_APP_WEBHOOKS_FLAG, false)

/** Whether the workspace Roles feature is enabled. Defaults OFF. */
export const useFeatureRoles = (): boolean =>
  useFeatureFlag(FEATURE_ROLES_FLAG, false)

/** Whether the workspace Public Access feature is enabled. Defaults OFF. */
export const useFeaturePublicAccess = (): boolean =>
  useFeatureFlag(FEATURE_PUBLIC_ACCESS_FLAG, false)

/** Whether the workspace AI connectors page is enabled. Defaults OFF. */
export const useFeatureMcpConnectors = (): boolean =>
  useFeatureFlag(FEATURE_MCP_CONNECTORS_FLAG, false)
